import { AdaptiveButton } from '../../components/AdaptiveElements';
import { connect } from 'react-redux';
import { createError, ReportedError } from '../../utils/error';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { setLanguage, toggleLanguageSelector } from '../../reducers';
import classNames from 'classnames';
import languageIcon from './images/icon@language@2x.png';
import LanguageModal from '../../components/LanguageModal';
import PropTypes from 'prop-types';
import React from 'react';

export const LANGUAGE_SELECTOR = {
  MODAL: 'MODAL',
  DROPDOWN_UP: 'DROPDOWN_UP',
  DROPDOWN_DOWN: 'DROPDOWN_DOWN',
};
const DROPDOWN_DIRECTION = { UP: 0, DOWN: 1 };

export class LanguageToggle extends React.PureComponent {
  constructor(props) {
    super(props);
    this.languageDropdownRef = React.createRef();
    this.languageModalRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    const { selectedLanguage } = this.props;
    if (prevProps.selectedLanguage.code !== selectedLanguage.code) {
      document.documentElement.lang = selectedLanguage.code.toLowerCase();
    }
  }

  renderLanguageModal() {
    const { languages, selectedLanguage, setLanguage, toggleLanguageSelector, isLanguageSelectorVisible } = this.props;
    return (
      <TransitionGroup component="div">
        {isLanguageSelectorVisible && (
          <CSSTransition key="language-modal" nodeRef={this.languageModalRef} classNames="modal" timeout={250}>
            <LanguageModal
              languages={languages}
              selectedLanguageCode={selectedLanguage.code}
              setLanguage={setLanguage}
              hideFn={toggleLanguageSelector}
              transitionRef={this.languageModalRef}
            />
          </CSSTransition>
        )}
      </TransitionGroup>
    );
  }

  renderLanguageDropdown(direction) {
    const { isLanguageSelectorVisible, languages, setLanguage, selectedLanguage, toggleLanguageSelector } = this.props;

    const getLanguageClass = (lang) =>
      classNames('button', 'button--dropdown', {
        'is-selected': lang.code === selectedLanguage.code,
      });

    const dropdownOptionsClass = classNames('dropdown__options', 'dropdown--language__options', {
      'directed-up': direction === DROPDOWN_DIRECTION.UP,
    });

    const languageOnClick = (lang) => {
      setLanguage(lang);
      toggleLanguageSelector();
    };

    return (
      <TransitionGroup component="div" className="dropdown--language">
        {isLanguageSelectorVisible && (
          <CSSTransition
            key="language-dropdown"
            nodeRef={this.languageDropdownRef}
            classNames="dropdown__options"
            timeout={250}
          >
            <div className={dropdownOptionsClass} ref={this.languageDropdownRef}>
              {languages.map((lang) => (
                <div
                  className={getLanguageClass(lang)}
                  onClick={() => languageOnClick(lang)}
                  role="button"
                  tabIndex={0}
                  key={lang.code}
                >
                  <span className="lang-name">{lang.name}</span>
                </div>
              ))}
            </div>
          </CSSTransition>
        )}
      </TransitionGroup>
    );
  }

  renderLanguageSelector() {
    const { languageSelector } = this.props;
    switch (languageSelector) {
      case LANGUAGE_SELECTOR.MODAL:
        return this.renderLanguageModal();
      case LANGUAGE_SELECTOR.DROPDOWN_UP:
        return this.renderLanguageDropdown(DROPDOWN_DIRECTION.UP);
      case LANGUAGE_SELECTOR.DROPDOWN_DOWN:
        return this.renderLanguageDropdown(DROPDOWN_DIRECTION.DOWN);
      default:
        throw createError(ReportedError, 'systemError', `Unexpected language selector: ${languageSelector}`);
    }
  }

  render() {
    const { languages, selectedLanguage, setLanguage, toggleLanguageSelector, className } = this.props;

    if (!languages || languages.length < 2) {
      return null;
    }

    // Quick switch if there are 2 languages.
    if (languages.length === 2) {
      const otherLanguage = languages.find((l) => l.code !== selectedLanguage.code);
      return (
        <AdaptiveButton className={`lang-button-wrapper ${className}`} onClick={() => setLanguage(otherLanguage)}>
          <img src={languageIcon} className="lang-button-wrapper-icon" alt="Change language" />
          <div className="lang-name">{otherLanguage.name}</div>
        </AdaptiveButton>
      );
    }

    return (
      <div className="lang-button-wrapper">
        <div className={className}>
          <AdaptiveButton onClick={() => toggleLanguageSelector()}>
            <img src={languageIcon} alt="Change language" />
            <span className="lang-button-text">{selectedLanguage.name}</span>
          </AdaptiveButton>
        </div>
        {this.renderLanguageSelector()}
      </div>
    );
  }
}

LanguageToggle.propTypes = {
  languages: PropTypes.array,
  selectedLanguage: PropTypes.object.isRequired,
  setLanguage: PropTypes.func.isRequired,
  languageSelector: PropTypes.oneOf(Object.values(LANGUAGE_SELECTOR)),
  toggleLanguageSelector: PropTypes.func.isRequired,
  isLanguageSelectorVisible: PropTypes.bool,
  className: PropTypes.string,
};

LanguageToggle.defaultProps = {
  languageSelector: LANGUAGE_SELECTOR.MODAL,
  isLanguageSelectorVisible: false,
};

function mapStateToProps(state) {
  return {
    languages: state.flow && state.flow.languages,
    isLanguageSelectorVisible: state.language.isSelectorVisible,
    selectedLanguage: state.language.currentLanguage,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    setLanguage: (language) => dispatch(setLanguage(language)),
    toggleLanguageSelector: () => dispatch(toggleLanguageSelector()),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(LanguageToggle);
